import { useState, useEffect, type FormEvent } from 'react';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { patchMyTutorProfile } from '@/lib/tutor-api';
import {
  EDUCATOR_SUBJECTS,
  GRADE_LEVELS,
  type EducatorSubject,
  type GradeLevel,
  type TutorProfileResponse,
} from '@/lib/schemas/tutor-profile';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ChipToggle } from './shared';

export function SubjectsCard({
  profile,
  onSaved,
}: {
  profile: TutorProfileResponse;
  onSaved: (p: TutorProfileResponse) => void;
}) {
  const { accessToken } = useAuth();
  const [subjects, setSubjects] = useState<EducatorSubject[]>(profile.subjects);
  const [gradeLevels, setGradeLevels] = useState<GradeLevel[]>(profile.gradeLevels);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setSubjects(profile.subjects);
    setGradeLevels(profile.gradeLevels);
  }, [profile.subjects, profile.gradeLevels]);

  const toggle = <T extends string>(value: T, list: T[], setter: (v: T[]) => void) =>
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (subjects.length === 0 || gradeLevels.length === 0) {
      setError('Select at least one subject and one grade level.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const sortedGradeLevels = [...gradeLevels].sort(
        (a, b) => GRADE_LEVELS.indexOf(a) - GRADE_LEVELS.indexOf(b)
      );
      const next = await patchMyTutorProfile(accessToken!, { subjects, gradeLevels: sortedGradeLevels });
      onSaved(next);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed — please retry.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">What You Teach</CardTitle>
        <CardDescription>Subjects and grade levels shown to parents in search results.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-6" onSubmit={submit}>
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          {success && <Alert variant="success"><AlertDescription>Saved — parents can see your updated subjects.</AlertDescription></Alert>}

          <fieldset>
            <legend className="mb-1 text-sm font-semibold text-foreground">Subjects</legend>
            <p className="mb-3 text-xs text-muted-foreground">Select all that apply</p>
            <div className="flex flex-wrap gap-2">
              {EDUCATOR_SUBJECTS.map((s) => (
                <ChipToggle key={s} label={s} selected={subjects.includes(s)} onToggle={() => toggle(s, subjects, setSubjects)} />
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-1 text-sm font-semibold text-foreground">Grade levels</legend>
            <p className="mb-3 text-xs text-muted-foreground">Select all you're comfortable teaching</p>
            <div className="flex flex-wrap gap-2">
              {GRADE_LEVELS.map((g) => (
                <ChipToggle key={g} label={g} selected={gradeLevels.includes(g)} onToggle={() => toggle(g, gradeLevels, setGradeLevels)} />
              ))}
            </div>
          </fieldset>

          <div className="flex justify-end border-t border-slate-100 pt-4 dark:border-slate-700">
            <Button type="submit" disabled={busy} className="min-w-[140px]">
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
